import React, { useState } from "react";

import { type HeadFC, type PageProps, graphql } from "gatsby";
import { AnchorLink } from "gatsby-plugin-anchor-links";

import Navigation from "../components/product/navigation";
import CustomerReviews from "../components/product/customerReviews";
import ProductOptions, { getPrices } from "../components/product/productOptions";
import FigmaDesign from "../components/product/figmaDesign";
import Carousel from "../components/product/carousel";

import Stars from "../components/shared/stars";
import Layout from "@/layouts/layout";
import Container from "../components/shared/container";

import ContactsSection from "../components/sections/index/contactsSection";

import AlsoProducts from "../components/product/alsoProducts";

import { __t } from "../utils/translate";
import { Product } from "../utils/types";

import HeadLinks from "@/components/shared/headLinks";

import Delivery from "@/images/icons/delivery.svg";
import { Schema } from "@/utils/schema";
import ProductProvider from "@/providers/productProvider";
import { useOptions } from "@/hooks/useOptions";

const addImagePropLazzy = (description: string) => {
    const re = /<img /gi;

    description = description.replace(re, '<img loading="lazy" decoding="async" ');
    return description;
}

const addPlaysInlineToVideoTag = (description: string) => {
    const videoTagRe = /<video\s+/gi;

    description = description.replace(videoTagRe, '<video playsinline="playsinline" ');
    return description;
}

type PageContextType = {
    slug: string;
    product: Product;
};

const ProductPage: React.FC<PageProps<unknown, PageContextType>> = ({
    pageContext,
    data
}) => {

    const product: Product = data.product;

    product.variations_product = product.variations_product.filter(item => item.status === 'publish');

    const prices = getPrices(product, product.defaultVariationID);
    const options = useOptions();
    const [currency_symbol, setCurrencySymbol] = useState(options.wpOptions.data.currency_symbol);

    const [sliderOpen, setSliderOpen] = useState(false);
    // const imageRef = useRef<HTMLImageElement>(null);

    const relatedProducts = product.related_products;
    const slicedRelatedProducts = relatedProducts.slice(0, 4);

    const getBundleAttributes = () => {
        const bundle = {
            products: {},
        };

        if (product.bundled_items) {
            product.bundled_items.forEach((item) => {
                bundle['bundle_quantity_' + item.bundled_item_id] = item.quantity_default;
                if (item.product) {
                    if (!bundle.products[item.bundled_item_id]) {
                        bundle.products[item.bundled_item_id] = {
                            variation_attributes: [],
                            default_attributes: {},
                            title: '',
                        };
                    }

                    item.product.default_attributes?.forEach((attribute) => {
                        const variation_attribute = item.product.variation_attributes.find((pr) => pr.id === attribute.id);
                        if (variation_attribute?.slug) {
                            bundle['bundle_attribute_' + variation_attribute.slug + '_' + item.bundled_item_id] = attribute.option;

                            bundle.products[item.bundled_item_id].default_attributes[variation_attribute.slug] = attribute.option;
                            bundle.products[item.bundled_item_id].title = item.product.title
                        }
                    });

                    const variation_attributes = item.product.variation_attributes;

                    if (variation_attributes.length > 0) {
                        bundle.products[item.bundled_item_id].variation_attributes = variation_attributes;
                    }

                    bundle.products[item.bundled_item_id] = item.product;
                }
            });
        }
        return bundle;
    }

    const [attributesBundle, setAttributesBundle] = useState(getBundleAttributes());

    const setAttributeBundle = (attribute) => {
        if (attribute) {
            setAttributesBundle((prevState) => {
                return { ...prevState, ...attribute, products: attributesBundle.products }
            })
        }
    };

    const description = product.acf.figma_description_enable
        ? null
        : addImagePropLazzy(addPlaysInlineToVideoTag(product.description));

    const reviews = data.allWcReviews.edges.map((edge) => edge.node).sort((a, b) => b.rating - a.rating);

    const renderPrices = () => {
        return (
            product.stock_status !== "instock"
                ? <div className={`flex flex-row gap-3 items-end text-title text-[21px] md:text-h3 md:mt-1 whitespace-nowrap`}>
                    {!!prices.sale_price && (
                        <>
                            <div className={`product_regular_price font-semibold md:line-through linetrough text-red`}>
                                <div className="inline"
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                />
                                {prices.regular_price}
                            </div>
                            <div className={`product_sale_price font-semibold text-button-accent-color`}>
                                <div className="inline"
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                />
                                {prices.sale_price}
                            </div>
                        </>
                    ) || (
                            <div className={`product_price font-semibold text-button-accent-color`}>
                                <div className="inline"
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                />
                                {prices.regular_price}
                            </div>
                        )}
                </div>
                : null
        )
    }

    return (
        <Layout className="single-product">
            <ProductProvider product={product}>
                <Carousel
                    isFullScreen={true}
                    product={product}
                    sliderOpen={sliderOpen}
                    setSliderOpen={() => setSliderOpen(!sliderOpen)}
                />

                <div className={`${product.acf.figma_description_enable ? 'bg-white' : 'bg-[#f8f8f8]'} w-full overflow-hidden`}>
                    <Container>
                        <section id='product-card' className="px-[5px]">
                            <Navigation
                                className="hidden lg:block xl:max-w-[1090px] xl:mx-auto"
                                currentPage={product.name}
                                collection={product.categories[0].name}
                                collectionSlug={`${product.categories[0].slug}`}
                                prefixSlug='/product-category/'
                            />

                            <div className="md:hidden flex flex-col mt-[5px] md:mt-0">
                                <div className="text-18 leading-6 font-bold text-dark" dangerouslySetInnerHTML={{ __html: product.name }} />
                                {renderPrices()}
                                <AnchorLink to="#reviews" title={__t("Rating and reviews")} >
                                    <div className="flex items-center gap-[12px] p-1">
                                        <Stars
                                            width="75"
                                            height="15"
                                            rating={Number(product.average_rating)}
                                        />
                                        <span className="text-small-14 text-black opacity-60">({product.rating_count} {__t('reviews')})</span>
                                    </div>
                                </AnchorLink>
                                <p className="mb-4 sm:mb-6">
                                    {product.total_sales} {__t("bought in past month")}
                                </p>
                            </div>

                            <div className="md:flex-row flex flex-col flex-wrap md:flex-nowrap md:gap-14 lg:gap-16 xl:gap-[40px] md:pt-4 md:pb-0 lg:px-6 xl:mx-auto xl:justify-center">
                                <div className="mb-5 md:mb-1 xl:ml-10">
                                    <Carousel
                                        isFullScreen={false}
                                        setSliderOpen={() => setSliderOpen(!sliderOpen)}
                                        className="md:max-w-[310px] lg:max-w-[383px] lg-hero:max-w-[450px] lg-hero:mx-[36px]"
                                        product={product}
                                    />
                                </div>
                                <div className="w-full overflow-visible md:overflow-hidden xl:max-w-[580px] xl:mr-12">

                                    <div className="hidden md:flex flex-col">
                                        <span className="text-button-accent-color text-base-16 font-semibold">{__t("Sale")}!</span>
                                        <h1 className="mb-1 text-dark text-h3-r" dangerouslySetInnerHTML={{ __html: product.name }} />
                                        {renderPrices()}
                                        <AnchorLink className="w-fit" to="#reviews" title={__t("Rating and reviews")} >
                                            <div className="mb-[10px] flex items-center gap-[12px]">
                                                <Stars
                                                    width="80"
                                                    height="15"
                                                    rating={Number(product.average_rating)}
                                                />
                                                <span className="text-small-14 text-black opacity-60">({product.rating_count} {__t('reviews')})</span>
                                            </div>
                                        </AnchorLink>
                                        <p className="mb-6">
                                            {product.total_sales} {__t("bought in past month")}
                                        </p>
                                    </div>

                                    <ProductOptions
                                        product={product}
                                        attributesBundle={attributesBundle}
                                        setAttributeBundle={setAttributeBundle}
                                        className={"overflow-visible"}
                                    />

                                    <div className="flex flex-row gap-[10px] items-center text-small-14 text-[#2BBB28] pt-1 xl:pt-3 mb-5 md:mb-0">
                                        <img
                                            src={Delivery}
                                            alt="Delivery"
                                        />
                                        {__t("Free shipping for all orders")}
                                    </div>

                                </div>
                            </div>
                        </section>
                    </Container>

                    <div className="flex flex-col items-center">

                        <div className="flex flex-col items-center  w-full">
                            {product.acf.figma_description_enable ? (
                                <FigmaDesign product={product} />
                            ) : (
                                <div className="w-full xl:bg-white">
                                    <Container>
                                        <div className="description-legacy px-[10px] py-[30px] bg-white md:px-4 lg:px-8 md:pb-6">
                                            <div
                                                className="flex flex-col justify-stretch lg:max-w-[74%] lg-hero:max-w-[65%] xl:max-w-[855px] mx-auto"
                                                dangerouslySetInnerHTML={{ __html: description }}
                                            ></div>
                                        </div>
                                    </Container>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <Container>
                    <div className="py-6 lg:py-4 w-full lg:max-w-[820px] xl:max-w-[1140px] mx-auto font-reviews" id="reviews">
                        <CustomerReviews
                            product={product}
                            className="lg:mt-[100px]"
                            reviews={reviews}
                        />
                    </div>
                </Container>



                {!!relatedProducts.length && (
                    <div className="w-full bg-light-gray">
                        <Container className="!px-0 lg:max-w-[1200px]">
                            <h2 className="pt-7 pb-5 lg:pb-9 lg:pt-10 text-dark text-center text-h2-r font-normal">
                                {__t("Related products")}
                            </h2>
                            <AlsoProducts products={slicedRelatedProducts} />
                        </Container>
                    </div>
                )}

                <ContactsSection />


            </ProductProvider>
        </Layout>
    );
};

export default ProductPage;

export const Head: HeadFC<unknown, PageContextType> = ({ pageContext, location, data }) => {
    return (
        <>
            <title>{pageContext.name}</title>
            <meta name="description" content={pageContext.aioseo_description} />
            <HeadLinks pathname={location.pathname} canonicalUrl={pageContext.canonical_url} />
            <Schema data={data} />
        </>
    )
};

export const query = graphql`
    query BlogPostBySlug($id: String!, $wordpress_id: Int!) {
        product:wcProducts(id: { eq: $id }) {
          id
          wordpress_id
          featured
          type
          canonical_url
          sku
          total_sales
          acf {
            sizeguide{
              image
              anchor
            }
            size_eyewear {
                name
                url
                ID
            }
            size_guide {
                name
                url
                ID
            }
            adventures_group{
              header
              text
              image {
                name
                url
                id
              }
            }
            description_group{
              header
              text
              image {
                name
                url
                id
              }
            }
            figma_description_enable
            first_description_block
            first_header
            video
            more_reasons_row_header
            more_reasons_group{
              image {
                name
                url
                id
              }
              text
            }
            measure{
              header
              image {
                name
                url
                id
              }
              sub_header
              table
              align_center
            }
            measure2{
              header
              image {
                name
                url
                id
              }
              sub_header
              table
              align_center
            }
            measure3{
              header
              image {
                name
                url
                id
              }
              sub_header
              table
              align_center
            }
            measure_steps{
              step
            }
            measure_steps2{
              step
            }
            measure_steps3{
              step
            }
            specification_header
            specification{
              only_title
              title
              value
            }
            faq {
                title
                items {
                  title
                  content
                }
              }
            disclaimer_header
            disclaimer
            first_block_bg
            adventures_group_bg
            description_group_bg
            more_reasons_group_bg
            measure_group_bg
            specification_group_bg
            faq_group_bg
            video_in_first_slide_url
          }
          image {
            src
            id
            alt
            name
            localFile {
              childrenImageSharp {
                gatsbyImageData(
                  breakpoints: [100, 360, 480, 660]
                  quality: 80
                  formats: [AUTO, WEBP]
                )
              }
            }
          }
          images {
            src
            id
            alt
            name
            localFile {
              childrenImageSharp {
                gatsbyImageData(
                  breakpoints: [100, 360, 480, 660]
                  quality: 80
                  formats: [AUTO, WEBP]
                )
              }
            }
          }
          price
          sale_price
          regular_price
          name
          permalink
          defaultVariationID
          default_attributes {
            id
            name
            option
          }
          attributes {
            id
            name
            options
            position
            variation
            visible
          }
          slug
          average_rating
          button_text
          description
          on_sale
          rating_count
          status
          stock_status
          categories {
            id
            name
            slug
          }
          tags {
            count
            description
            id
            name
            slug
          }
          variation_attributes{
            id
            position
            visible
            options{
              name
              slug
            }
            name
            slug
          }
          variations_product {
            image {
              name
              src
              id
              alt
              localFile {
                childrenImageSharp {
                  gatsbyImageData(
                    breakpoints: [100, 360, 480, 660]
                    quality: 80
                    formats: [AUTO, WEBP]
                  )
                }
              }
            }
            id
            menu_order
            description
            sale_price
            regular_price
            manage_stock
            stock_quantity
            stock_status
            #attributes {
            #  id
            #  name
            #  option
            #}
            attributes2 {
              attribute
              value
              value_label
              attribute_label
            }
            status

          }
          bundled_items{
              bundled_item_id
              product_id
              quantity_min
              quantity_max
              quantity_default
              default_variation_attributes {
                id
                name
                option
              }
              product{
                wordpress_id
                name
                image {
                  src
                  id
                  alt
                  name
                  localFile {
                    childrenImageSharp {
                      gatsbyImageData(
                        breakpoints: [100, 360, 480, 660]
                        quality: 80
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
                defaultVariationID
                default_attributes {
                  id
                  name
                  option
                }
                variation_attributes{
                  id
                  position
                  visible
                  options{
                    name
                    slug
                  }
                  name
                  slug
                }
                variations_product {
                  image {
                    name
                    src
                    id
                    localFile {
                      childrenImageSharp {
                        gatsbyImageData(
                          breakpoints: [100, 360, 480, 660]
                          quality: 80
                          formats: [AUTO, WEBP]
                        )
                      }
                    }
                  }
                  id
                  menu_order
                  description
                  sale_price
                  regular_price
                  stock_status
                  stock_quantity
                  manage_stock
                  #attributes {
                  #  id
                  #  name
                  #  option
                  #}
                  attributes2 {
                    attribute
                    value
                  }
                  status
                }
              }
          }
          related_products {
            id
            image {
              src
              id
              alt
              name
              localFile {
                childrenImageSharp {
                  gatsbyImageData(
                    breakpoints: [300]
                    quality: 80
                    formats: [AUTO, WEBP]
                  )
                }
              }
            }
            price
            sale_price
            regular_price
            name
            permalink
            defaultVariationID
            default_attributes {
              id
              name
              option
            }
            attributes {
              id
              name
              options
              position
              variation
              visible
            }
            slug
            average_rating
            button_text
            description
            on_sale
            rating_count
            status
            stock_status
            categories {
              id
              name
              slug
            }
            tags {
              count
              description
              id
              name
              slug
            }
            variations_product {
              image {
                name
                src
                id
                localFile {
                  childrenImageSharp {
                    gatsbyImageData(
                      quality: 100
                      formats: [AUTO, WEBP]
                      width: 60
                    )
                  }
                }
              }
              id
              menu_order
              description
              sale_price
              regular_price
              attributes2 {
                attribute
                value
              }
              status
            }
          }
        }
        allWcReviews(filter: {product_id: {eq: $wordpress_id}}) {
          edges{
            node{
              id
              product_id
              product_name
              rating
              review
              reviewer
              status
              verified
              date_created
            }
          }
        }
    }
`
